import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { WorldReady } from '@paypalcorp/worldready';
import { WorldReadyProvider } from '@paypalcorp/pp-react-worldready';
import { ContextProvider as WorldReadyContextProvider } from '@paypalcorp/worldready-react';
import { init as initServerData, Data as ServerData } from '@client/utils/server-data';
import { CONTEXT_ROOT } from '@client/lib/constants';
import { WorldReadyProps } from '../utils/localeHelper';

type InitialAppData = {
  app: {
    locale: string;
    worldReady: WorldReadyProps;
  };
  serverData: ServerData;
};

export const initializeAppData = (initialData?: InitialAppData): InitialAppData => {
  // eslint-disable-next-line no-underscore-dangle
  const initialState = initialData || (window.__initialState__ as InitialAppData);

  // Load localization messages from WorldReady
  const worldReadyData = initialState?.app?.worldReady;
  WorldReady.load(worldReadyData);
  initServerData(initialState?.serverData);

  // Allow the passed state to be garbage-collected
  // eslint-disable-next-line no-underscore-dangle
  if (!initialData) {
    delete window.__initialState__;
  }

  return initialState;
};

export const hydrate = ({
  App,
  initialAppData,
  options = {
    hydrateMethod: 'rootRender'
  }
}: {
  App: React.FunctionComponent<{ initialAppData: InitialAppData }>;
  initialAppData: InitialAppData;
  options?: {
    hydrateMethod: 'hydrateRoot' | 'rootRender';
  };
}) => {
  const container = document.getElementById('root');
  if (container) {
    const Comp = (
      <WorldReadyContextProvider locale={initialAppData.app.locale}>
        <WorldReadyProvider>
          <BrowserRouter basename={CONTEXT_ROOT}>
            <App initialAppData={initialAppData} />
          </BrowserRouter>
        </WorldReadyProvider>
      </WorldReadyContextProvider>
    );

    if (options.hydrateMethod === 'hydrateRoot') {
      hydrateRoot(container, Comp);
    } else {
      const root = createRoot(container);
      root.render(Comp);
    }
  }
};
